import { useState } from "react";
import { useForm } from "react-hook-form";

import FilterCollapse from "components/filter_form/filter_collapse/FilterCollapse";
import FilterCollapseHeader from "components/filter_form/filter_collapse_header/FilterCollapseHeader";
import FilterFormTitle from "components/filter_form/filter_form_title/FilterFormTitle";
import { countFilters } from "components/filter_form/utils";
import { camelToSentence } from "lib/utils";
import { useFormAutoSubmit } from "../../../lib/hooks/useFormAutoSubmit";
import { LeadFilterContents } from "./filter_groups/utils";
import { EMPTY_LEAD_FILTERS, LeadFilters } from "./utils";

const groupedKeys: Record<FilterGroupKeys, (keyof LeadFilters)[]> = {
  assignedTo: ["assignee"],
  team: ["teamIds"],
  stage: ["stageIds"],
  value: ["value"],
  createdDate: ["createdDate"],
  lastUpdated: ["lastUpdatedDate"],
  expiryDate: ["expiryDate"],
  buyers: ["buyers", "customBuyers", "buyerLists", "buyerCategories", "buyerCountryRegions"],
  suppliers: ["supplierGuids", "supplierSme"],
  createdBy: ["createdBy"],
};

type FilterGroupKeys = keyof typeof LeadFilterContents;

type FilterFormProps = {
  onChange: (filters: LeadFilters) => void;
  filters: LeadFilters;
  hiddenFilters?: FilterGroupKeys[];
  onClose: () => void;
};

export function FilterForm({ onChange, filters, onClose, hiddenFilters }: FilterFormProps) {
  const [activeGroups, setActiveGroups] = useState<string[]>([]);

  const { control, watch, trigger, getValues, reset, setValue } = useForm<LeadFilters>({
    mode: "onChange",
    defaultValues: filters,
  });

  const data = watch();

  useFormAutoSubmit({
    watch,
    trigger,
    getValues,
    onChange,
  });

  const counts = countFilters(filters, groupedKeys);

  const clearFilter = (key: FilterGroupKeys) => {
    const keys = groupedKeys[key];
    keys.forEach((k) => {
      setValue(k, EMPTY_LEAD_FILTERS[k]);
    });
  };

  const getFilterGroups = () => {
    let items = (Object.keys(groupedKeys) as FilterGroupKeys[]).map((key) => {
      const ContentComponent = LeadFilterContents[key];

      return {
        key,
        header: (
          <FilterCollapseHeader
            title={camelToSentence(key)}
            count={counts[key]}
            clear={() => clearFilter(key)}
          />
        ),
        content: <ContentComponent control={control} data={data} />,
      };
    });

    if (hiddenFilters?.length) {
      items = items.filter((item) => !hiddenFilters.includes(item.key));
    }

    return items;
  };

  const items = getFilterGroups();

  return (
    <form aria-label="Lead filters">
      <FilterFormTitle
        clearFilters={() => reset({ ...EMPTY_LEAD_FILTERS, searchText: filters.searchText })}
        showClear={Object.values(counts).some(Boolean)}
        onClose={onClose}
      />
      <FilterCollapse items={items} value={activeGroups} onChange={setActiveGroups} />
    </form>
  );
}
